import { Box, Flex } from '@lego/klik-ui';
import { Navigate, useParams } from 'react-router-dom';
import { EditableApplicationDetails } from '@/components/application/EditableApplicationDetails';
import { RestrictedKeyRequestTable } from '@/components/application/RestrictedKeyRequestTable';
import { ApplicationVersionTable } from '@/components/application_version/ApplicationVersionTable';
import { Loader, PageLoadError } from '@/components/common';
import { BreadCrumb } from '@/components/common/BreadCrumb';
import { useApplication } from '@/hooks/application';
import { useDocumentTitle } from '@/hooks/common';
import { useRegisterPageContext } from '@/providers/PageContextProvider';
import { paths } from '@/utility/global_paths';

const ApplicationPageContent = ({
  workspaceId,
  applicationId,
}: {
  workspaceId: number;
  applicationId: number;
}) => {
  const { data, error, isLoading, mutate } = useApplication(workspaceId, applicationId);
  useRegisterPageContext(data);
  useDocumentTitle(data?.name ?? `Application #${applicationId}`);

  if (isLoading) {
    return (
      <Box>
        <Loader />
      </Box>
    );
  }

  if (error || !data) {
    return (
      <PageLoadError
        backAction={{ label: 'Back to workspace', to: paths.workspaceById(workspaceId) }}
        onRetry={mutate}
      />
    );
  }

  const application = data;

  return (
    <Box>
      <Box>
        <Box>
          <BreadCrumb
            crumbs={[
              { label: 'Workspaces', path: paths.workspaces },
              {
                label: `Workspace #${application.workspaceId}`,
                path: paths.workspaceById(application.workspaceId),
              },
              {
                label: `Application #${application.id}`,
                path: paths.applicationById(application.workspaceId, application.id),
              },
            ]}
          />
        </Box>
        <Flex direction="column" gap={8} marginY={8}>
          <EditableApplicationDetails application={application} />
          <ApplicationVersionTable application={application} />
          <RestrictedKeyRequestTable
            applicationId={application.id}
            workspaceId={application.workspaceId}
          />
        </Flex>
      </Box>
    </Box>
  );
};

export const ApplicationPage = () => {
  const { workspaceId, applicationId } = useParams();

  if (
    !workspaceId ||
    !Number.parseInt(workspaceId, 10) ||
    !applicationId ||
    !Number.parseInt(applicationId, 10)
  ) {
    return <Navigate to={paths.notFound} />;
  }

  return (
    <ApplicationPageContent
      applicationId={Number.parseInt(applicationId, 10)}
      workspaceId={Number.parseInt(workspaceId, 10)}
    />
  );
};
